// pages/editorials/EditorialIndexPage.tsx
import { Box, Container, Input, Stack, Text, useMounted } from '@qcx/ui';
import { Breadcrumb, BreadcrumbLink } from '../../components/Breadcrumb';
import { EditorialCard } from './components/EditorialCard';
import { EditorialsLinks } from './components/EditorialsLinks';
import { EditorialsService } from '@atreus/data';
import { Page } from '../../components/page/Page';
import { Pagination } from '@kratos/remix-components';
// eslint-disable-next-line import/no-unresolved
import { Route } from './+types/EditorialsIndexPage';
import { SearchNoResults } from '../../components/SearchNoResults';
import { generateCanonicalUrl } from '../../lib/generate-canonical-url.lib';
import { getCookie } from '../../lib/headers.lib';
import { useCallback, useEffect, useState } from 'react';
import { useEditorialsStore } from './editorials.store';
import { useNavigate } from 'react-router';

type KindValue = 'institute' | 'region' | 'editorial';

export const meta = ({ data }: Route.MetaArgs) => {
  if (!data) {
    return [];
  }

  const currentPage = data.meta.currentPage;
  const previousPage = currentPage - 1;
  const nextPage = currentPage + 1;
  const baseUrl = data.baseUrl;
  const canonicalUrl = generateCanonicalUrl(data.url);
  const pageQuery = currentPage === 2 ? '' : `?page=${previousPage}`;
  const relTags = [];

  relTags.push({
    tagName: 'link',
    rel: 'canonical',
    href: canonicalUrl
  });

  if (currentPage > 1) {
    relTags.push({
      tagName: 'link',
      rel: 'prev',
      href: baseUrl + '/editorias' + pageQuery
    });
  }

  if (currentPage !== data.meta.totalPages) {
    relTags.push({
      tagName: 'link',
      rel: 'next',
      href: baseUrl + '/editorias?page=' + nextPage
    });
  }

  return [
    { title: 'Folha Dirigida - Notícias para Concursos Públicos' },
    {
      name: 'description',
      content:
        'Todas as últimas notícias de concursos públicos. Notícias atualizadas e disponíveis dos principais concursos públicos do Brasil, em primeira mão.'
    },
    { name: 'url', content: data.url },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Início',
            item: data.baseUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Editoria',
            item: `${data.baseUrl}/editorias`
          }
        ]
      }
    },
    ...relTags
  ];
};

export const loader = async ({ request }: Route.LoaderArgs) => {
  const url = new URL(request.url);
  url.protocol = 'https';
  const page = Number(url.searchParams.get('page') ?? 1);
  const q = url.searchParams.get('q') ?? '';
  const kind = url.searchParams.get('tipo') as
    | 'institutos'
    | 'regioes'
    | 'especiais'
    | undefined;

  const kindValue = kind
    ? ({
        institutos: 'institute',
        regioes: 'region',
        especiais: 'editorial'
      }[kind] as KindValue)
    : undefined;

  const editorialsService = new EditorialsService({
    headers: getCookie(request.headers)
  });
  const { categories, meta } = await editorialsService.listAtreusEditorials({
    kind: kindValue,
    q,
    page,
    perPage: 10
  });
  const contentGroup = 'fd category index';

  return {
    query: q,
    kind,
    baseUrl: url.origin,
    url: url.toString(),
    categories,
    meta,
    contentGroup
  };
};

const EditorialsIndexPage = ({ loaderData }: Route.ComponentProps) => {
  const { query, kind, url, categories, meta, contentGroup } = loaderData;

  const [searchValue, setSearchValue] = useState(query);
  const navigate = useNavigate();

  const getPath = useCallback(
    (query: string) => {
      const urlClient = new URL(url);
      urlClient.searchParams.set('q', query.toString());

      if (query) {
        navigate(`${urlClient.pathname}${urlClient.search}`);
      }
    },
    [navigate, url]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      getPath(searchValue);
    }, 350);

    return () => {
      clearTimeout(timer);
    };
  }, [searchValue, getPath]);

  const setUserPreferences = useEditorialsStore(s => s.setUserPreferences);

  useMounted(() => {
    setUserPreferences();

    if (window?.zaraz) {
      window.zaraz.set('content_group', contentGroup);
    }
  });

  return (
    <Page>
      <Container>
        <Breadcrumb>
          <BreadcrumbLink href={'/'}>Início</BreadcrumbLink>
          <BreadcrumbLink href={'/editorias'} isCurrentPage>
            Editorias
          </BreadcrumbLink>
        </Breadcrumb>
      </Container>
      <Container as="section">
        <Text
          as="h1"
          css={{ color: '$text', mb: '$4' }}
          variant="displayMd"
          weight="bold"
        >
          Editorias mais lidas
        </Text>
        <Box css={{ '@lg': { d: 'flex', flexDirection: 'row', gap: 20 } }}>
          <Box css={{ d: 'flex', flexDirection: 'column', gap: 15 }}>
            <Box>
              <Text css={{ color: '$text' }} variant={'sm'} weight={'medium'}>
                Busque por uma editoria
              </Text>
              <Input
                css={{
                  w: '$full',
                  mt: '$2',
                  '@lg': { d: 'block' }
                }}
                defaultValue={query}
                placeholder="Ex: Sudeste 2024"
                type="text"
                onChange={e => {
                  setSearchValue(e.target.value);
                }}
              />
            </Box>
            <EditorialsLinks kind={kind} />
          </Box>
          <Stack
            flow={{
              '@initial': 'column',
              '@lg': 'row'
            }}
            gap="$8"
          >
            <Stack
              css={{
                py: '$5',
                '@lg': {
                  w: 880
                }
              }}
              flow="column"
              gap="$5"
            >
              {meta.totalCount === 0 ? (
                <SearchNoResults searchTerm={query} subject="editoria" />
              ) : (
                categories.map(editorial => (
                  <EditorialCard key={editorial.id} editorial={editorial} />
                ))
              )}

              <Box
                css={{
                  flexColCenter: true,
                  mt: '$10'
                }}
              >
                {meta.totalCount > 10 && (
                  <Pagination
                    pagination={meta}
                    permittedParams={['page', 'q']}
                    url={url}
                  />
                )}
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Page>
  );
};

export default EditorialsIndexPage;
